import { useAuthStore } from '@/stores'
import axios, { AxiosResponse, AxiosRequestConfig, AxiosError } from 'axios'
import type { ApiErrorResponse } from '@/types/services'
import configService from './config.service'
import { showApiErrorNotification } from '@/utils/globalNotifications'

/**
 * API Service configuration options
 */
interface ApiServiceOptions {
  showNotifications?: boolean
  notificationOptions?: Record<string, any>
}

/**
 * Error handling options for API requests
 */
interface ErrorHandlingOptions {
  showNotification?: boolean
  notificationOptions?: Record<string, any>
}

/**
 * Spring Boot error response structure
 */
interface SpringBootErrorResponse {
  message?: string
  errorCode?: string
  errors?: Record<string, string>
  fieldErrors?: Array<{
    field: string
    message?: string
    defaultMessage?: string
  }>
  path?: string
}

/**
 * Extended Axios config with error handling options
 */
interface ExtendedAxiosConfig extends AxiosRequestConfig {
  errorHandling?: ErrorHandlingOptions
}

/**
 * Check if we should use mock services based on configuration
 * This is now determined by the config.json file instead of environment variables
 */
let useMocks = false // Will be updated during initialization

/**
 * Initialize the API service with configuration
 */
async function initializeUseMocks(): Promise<void> {
  try {
    const config = await configService.getConfig()
    // For now, set to false until we add this to the config types
    useMocks = false // config.FEATURES?.enableMockServices || false
    console.log('API Service configuration:', { useMocks })
  } catch (error) {
    console.warn('Failed to load API configuration, using defaults:', error)
    useMocks = false
  }
}

// Initialize on module load
initializeUseMocks()

/**
 * Base API service that handles common functionality for API calls
 */
export default class ApiService {
  private readonly showNotifications: boolean
  private readonly notificationOptions: Record<string, any>

  constructor(options: ApiServiceOptions = {}) {
    this.showNotifications = options.showNotifications !== false // Default to true
    this.notificationOptions = options.notificationOptions || {}
  }

  /**
   * Get the authorization headers for API requests
   */
  getHeaders(customHeaders: Record<string, string> = {}): Record<string, string> {
    // Merge default headers with custom headers
    const authStore = useAuthStore()
    return {
      'Authorization': `Bearer ${authStore.getAccessToken}`,
      'Content-Type': 'application/json',
      ...customHeaders // Custom headers will override defaults if provided
    }
  }

  /**
   * Handle API error responses
   */
  handleError(error: AxiosError, options: ErrorHandlingOptions = {}): Promise<never> {
    console.error('API Error:', error)
    
    // Network or request setup errors
    if (!error.response) {
      const networkError: ApiErrorResponse = {
        message: error.message || 'Network error occurred',
        code: 'NETWORK_ERROR',
        timestamp: new Date().toISOString()
      }
      
      // Show notification if enabled
      if (this.showNotifications && options.showNotification !== false) {
        showApiErrorNotification({
          type: 'NETWORK_ERROR',
          message: networkError.message,
          canRetry: true,
          code: 'NETWORK_ERROR',
          timestamp: new Date().toISOString()
        }, {
          ...this.notificationOptions,
          ...options.notificationOptions
        })
      }
      
      return Promise.reject(networkError)
    }

    const { status, data } = error.response
    const errorResponse: ApiErrorResponse = {
      message: 'An unexpected error occurred',
      code: 'API_ERROR',
      timestamp: new Date().toISOString(),
      details: {
        status,
        originalError: error
      }
    }

    // Handle structured error responses from Spring Boot
    if (data && typeof data === 'object') {
      const errorData = data as SpringBootErrorResponse
      
      // Main error message
      errorResponse.message = errorData.message || 'An unexpected error occurred'

      // Error code if available
      if (errorData.errorCode) {
        errorResponse.code = errorData.errorCode

        // Try to resolve a localized message from i18n using the code
        try {
          // window.i18n is set in main.ts during app bootstrap
          const win: any = window as any
          if (win && win.i18n && win.i18n.global && typeof win.i18n.global.t === 'function') {
            // Build translation key under errors.codes.<CODE>
            const translationKey = `errors.codes.${errorData.errorCode}`

            // If backend provided params in errors map or message placeholders, try to supply them
            // For now we assume no structured params from backend, but fieldErrors/details might contain values.
            const translated = win.i18n.global.t(translationKey)
            // vue-i18n returns the key unchanged when missing (see missing handler), so ensure we only use it when different
            if (translated && translated !== translationKey) {
              errorResponse.message = translated as string
            }
          }
        } catch (e) {
          // Ignore i18n resolution errors and keep existing message
          console.warn('i18n translation lookup failed for error code', errorData.errorCode, e)
        }
      }
      
      // Check for file size limit errors
      if (errorData.message && errorData.message.includes('SizeLimitExceededException')) {
        errorResponse.code = 'FILE_TOO_LARGE'
        errorResponse.message = 'File size exceeds the maximum allowed limit (10MB)'
      }
      
      // Field validation errors (from MethodArgumentNotValidException)
      // Backend sends 'errors' as Map<String, String>, but some components expect 'fieldErrors' array
      if (errorData.errors && typeof errorData.errors === 'object') {
        errorResponse.details = {
          ...errorResponse.details,
          fieldErrors: errorData.errors, // Keep as object for new components
          validationErrors: errorData.errors // Alternative name for consistency
        }
        errorResponse.code = errorResponse.code || 'VALIDATION_ERROR'
      }
      
      // Also handle if backend sends fieldErrors array (for backward compatibility)
      if (errorData.fieldErrors && Array.isArray(errorData.fieldErrors)) {
        const fieldErrorMap: Record<string, string> = {}
        errorData.fieldErrors.forEach((fieldError) => {
          fieldErrorMap[fieldError.field] = fieldError.message || fieldError.defaultMessage || 'Validation error'
        })
        errorResponse.details = {
          ...errorResponse.details,
          fieldErrors: fieldErrorMap,
          validationErrors: fieldErrorMap
        }
        errorResponse.code = errorResponse.code || 'VALIDATION_ERROR'
      }
      
      // Path information
      if (errorData.path) {
        errorResponse.details = {
          ...errorResponse.details,
          path: errorData.path
        }
      }
    } else {
      // Fallback for non-JSON responses
      errorResponse.message = `HTTP ${status}: ${error.response.statusText || 'Unknown error'}`
    }

    // Add specific handling for common HTTP status codes
    switch (status) {
      case 400:
        errorResponse.code = errorResponse.code || 'BAD_REQUEST'
        break
      case 401:
        errorResponse.code = 'UNAUTHORIZED'
        errorResponse.message = errorResponse.message || 'Authentication required'
        break
      case 403:
        errorResponse.code = 'FORBIDDEN'
        errorResponse.message = errorResponse.message || 'Access denied'
        break
      case 404:
        errorResponse.code = 'NOT_FOUND'
        errorResponse.message = errorResponse.message || 'Resource not found'
        break
      case 413:
        // HTTP 413 Payload Too Large - commonly used for file size limits
        errorResponse.code = 'FILE_TOO_LARGE'
        errorResponse.message = 'File size exceeds the maximum allowed limit (10MB)'
        break
      case 409:
        errorResponse.code = 'CONFLICT'
        break
      case 422:
        errorResponse.code = 'UNPROCESSABLE_ENTITY'
        break
      case 429:
        errorResponse.code = 'TOO_MANY_REQUESTS'
        errorResponse.message = errorResponse.message || 'Too many requests, please try again later'
        break
      case 500:
        errorResponse.code = 'INTERNAL_SERVER_ERROR'
        errorResponse.message = errorResponse.message || 'Internal server error occurred'
        break
      case 502:
      case 503:
      case 504:
        errorResponse.code = 'SERVICE_UNAVAILABLE'
        errorResponse.message = errorResponse.message || 'Service temporarily unavailable'
        break
    }

    // Show notification if enabled and not a validation error (components should handle those)
    // Try to resolve a localized message based on the final error code (covers status-based codes)
    try {
      const win: any = window as any
      if (win && win.i18n && win.i18n.global && typeof win.i18n.global.t === 'function' && errorResponse.code) {
        const translationKey = `errors.codes.${errorResponse.code}`
        const translated = win.i18n.global.t(translationKey)
        if (translated && translated !== translationKey) {
          errorResponse.message = translated as string
        }
      }
    } catch (e) {
      // ignore
    }

    if (this.showNotifications && 
        options.showNotification !== false && 
        errorResponse.code !== 'VALIDATION_ERROR') {
      showApiErrorNotification({
        ...errorResponse,
        type: errorResponse.code?.toString() || 'API_ERROR',
        canRetry: false
      }, {
        ...this.notificationOptions,
        ...options.notificationOptions
      })
    }

    return Promise.reject(errorResponse)
  }

  /**
   * Make a GET request to the API that returns the full response
   */
  async getWithHeaders<T = any>(
    url: string, 
    params: Record<string, any> = {}, 
    config: ExtendedAxiosConfig = {}
  ): Promise<AxiosResponse<T> | null> {
    // If mock services are enabled, log the attempted request and return null
    if (useMocks) {
      console.log(`[MOCK] GET request would have been made to: ${url}`, params)
      return null
    }
    
    try {
      const response = await axios.get<T>(url, {
        params,
        headers: this.getHeaders(config.headers as Record<string, string>),
        ...config
      })
      return response
    } catch (error) {
      return this.handleError(error as AxiosError, config.errorHandling)
    }
  }

  /**
   * Make a GET request to the API
   */
  async get<T = any>(
    url: string, 
    params: Record<string, any> = {}, 
    config: ExtendedAxiosConfig = {}
  ): Promise<T | null> {
    // If mock services are enabled, log the attempted request and return null
    if (useMocks) {
      console.log(`[MOCK] GET request would have been made to: ${url}`, params)
      return null
    }
    
    try {
      const response = await axios.get<T>(url, {
        params,
        headers: this.getHeaders(config.headers as Record<string, string>),
        ...config
      })
      return response.data
    } catch (error) {
      return this.handleError(error as AxiosError, config.errorHandling)
    }
  }

  /**
   * Make a POST request to the API
   */
  async post<T = any>(
    url: string, 
    data: any, 
    config: ExtendedAxiosConfig = {}
  ): Promise<T | null> {
    // If mock services are enabled, log the attempted request and return null
    if (useMocks) {
      console.log(`[MOCK] POST request would have been made to: ${url}`, data)
      return null
    }
    
    try {
      // Special handling for FormData to avoid Content-Type conflicts
      let headers = config.headers as Record<string, string> || {}
      
      // For FormData, don't set Content-Type as axios will set it with the boundary
      if (data instanceof FormData) {
        // Only include Authorization header, not Content-Type for multipart/form-data
        const authStore = useAuthStore()
        headers = { 
          'Authorization': `Bearer ${authStore.getAccessToken}`,
          // Let browser set the content type with boundary
          ...headers
        }
        
        // Remove Content-Type if explicitly set to avoid conflicts
        delete headers['Content-Type']
      } else {
        // For regular JSON data, use standard headers
        headers = this.getHeaders(headers)
      }
      
      const response = await axios.post<T>(url, data, {
        headers: headers,
        ...config
      })
      return response.data
    } catch (error) {
      return this.handleError(error as AxiosError, config.errorHandling)
    }
  }

  /**
   * Make a PUT request to the API
   */
  async put<T = any>(
    url: string, 
    data: any, 
    config: ExtendedAxiosConfig = {}
  ): Promise<T | null> {
    // If mock services are enabled, log the attempted request and return null
    if (useMocks) {
      console.log(`[MOCK] PUT request would have been made to: ${url}`, data)
      return null
    }
    
    try {
      // Special handling for FormData to avoid Content-Type conflicts
      let headers = config.headers as Record<string, string> || {}
      
      // For FormData, don't set Content-Type as axios will set it with the boundary
      if (data instanceof FormData) {
        // Only include Authorization header, not Content-Type for multipart/form-data
        const authStore = useAuthStore()
        headers = { 
          'Authorization': `Bearer ${authStore.getAccessToken}`,
          // Let browser set the content type with boundary
          ...headers
        }
        
        // Remove Content-Type if explicitly set to avoid conflicts
        delete headers['Content-Type']
      } else {
        // For regular JSON data, use standard headers
        headers = this.getHeaders(headers)
      }
      
      const response = await axios.put<T>(url, data, {
        headers: headers,
        ...config
      })
      return response.data
    } catch (error) {
      return this.handleError(error as AxiosError, config.errorHandling)
    }
  }

  /**
   * Make a DELETE request to the API
   */
  async delete<T = any>(
    url: string, 
    data: any = null, 
    config: ExtendedAxiosConfig = {}
  ): Promise<T | null> {
    // If mock services are enabled, log the attempted request and return null
    if (useMocks) {
      console.log(`[MOCK] DELETE request would have been made to: ${url}`, data)
      return null
    }
    
    try {
      const axiosConfig: AxiosRequestConfig = {
        headers: this.getHeaders(config.headers as Record<string, string>),
        ...config
      }
      if (data) {
        axiosConfig.data = data
      }
      const response = await axios.delete<T>(url, axiosConfig)
      return response.data
    } catch (error) {
      return this.handleError(error as AxiosError, config.errorHandling)
    }
  }

  /**
   * Make a GET request to the API that returns a blob (for file downloads)
   */
  async getBlob(
    url: string, 
    params: Record<string, any> = {}, 
    config: ExtendedAxiosConfig = {}
  ): Promise<Blob | null> {
    // If mock services are enabled, log the attempted request and return null
    if (useMocks) {
      console.log(`[MOCK] GET blob request would have been made to: ${url}`, params)
      return null
    }
    
    try {
      const response = await axios.get(url, {
        params,
        headers: this.getHeaders(config.headers as Record<string, string>),
        responseType: 'blob',
        ...config
      })
      return response.data
    } catch (error) {
      return this.handleError(error as AxiosError, config.errorHandling)
    }
  }
}

export type { ApiServiceOptions, ErrorHandlingOptions, ExtendedAxiosConfig }
